import PropTypes from 'prop-types';
import React, { useState } from 'react';

import ActionsMenu from '~/components/Shared/ActionsMenu/ActionsMenu';
import { Button } from '~/eds/src/components/Button/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import Card from '~/ui/components/Card/index';
import EditableText from '~/ui/components/EditableText/EditableText';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Text from '~/ui/components/Text/Text';
import { PHASE_TITLE_CHARACTER_LIMIT } from '~/ui/enums/input';
import { useModalSimple, useModalSortable } from '~/ui/hooks/modal';
import { toSnakeTrimmed } from '~/utils/strings';

function Header({
  disabled,
  id,
  isTitleInvalid,
  rightContent,
  title,
  onEditTitle,
}) {
  const intl = useIntl();
  const headerTitle = (
    <EditableText
      disabled={disabled}
      id={id}
      placeholder={intl.formatMessage({
        id: 'StageItems.untitledSection',
        defaultMessage: 'Untitled Section',
        description: 'Placeholder text for section title',
      })}
      value={title}
      onChange={onEditTitle}
      error={
        isTitleInvalid
          ? intl.formatMessage(
              {
                id: 'StageItems.titleCharacterLimit',
                defaultMessage:
                  'Your title may contain up to {limit} characters.',
                description:
                  'Error message for title character limit. Example: limit = 50',
              },
              { limit: PHASE_TITLE_CHARACTER_LIMIT },
            )
          : null
      }
    />
  );

  return (
    <Card.Header id={id} rightContent={rightContent} title={headerTitle} />
  );
}

function StageItems({
  disabled,
  items,
  invalidPhaseIds,
  newItemAction,
  removeModalTitle,
  sortModalTitle,
  onEditTitle,
  onRemove,
  onSort,
}) {
  const intl = useIntl();
  const [selectedItem, setSelectedItem] = useState({});

  const [sortableListModal, showSortableListModal] = useModalSortable({
    confirm: (sortedItems) =>
      onSort(sortedItems.map((sortedItem) => sortedItem.value)),
    items: items.map((item) => ({ value: item.id, label: item.title })),
    text: intl.formatMessage({
      id: 'StageItems.sort',
      defaultMessage: 'Sort',
      description: 'Button text for sorting items',
    }),
    title: sortModalTitle,
  });

  const [removeModal, showRemoveModal] = useModalSimple({
    confirm: () => onRemove(selectedItem.id),
    text: intl.formatMessage({
      id: 'StageItems.remove',
      defaultMessage: 'Remove',
      description: 'Button text for removing items',
    }),
    title: removeModalTitle,
    content: (
      <FlexLayout py={2}>
        <FormattedMessage
          id="StageItems.removeConfirmation"
          defaultMessage="Are you sure you want to remove <strong>{title}</strong>?"
          description="Confirmation message for removing an item. Example: title = 'Stage 1'"
          values={{
            strong: (chunks) => <Text variant="xs-dense-bold">{chunks}</Text>,
            title: selectedItem.title,
          }}
        />
      </FlexLayout>
    ),
  });

  return (
    <FlexLayout flexDirection="column" space={6}>
      {items.map((item) => {
        const { id, title, content } = item;
        const titleName = toSnakeTrimmed(title);

        const getMenuItems = () => {
          const menuItems = [];

          menuItems.push({
            content: sortModalTitle,
            disabled: items.length === 1 || disabled,
            onClick: () => showSortableListModal(),
          });

          menuItems.push({
            content: removeModalTitle,
            disabled: disabled,
            onClick: () => {
              setSelectedItem(item);
              showRemoveModal();
            },
          });

          return menuItems;
        };

        return (
          <Card
            key={id}
            header={
              <Header
                disabled={disabled}
                id={`workflow-form-section-name=${titleName}-title-edit`}
                rightContent={
                  disabled ? null : (
                    <ActionsMenu items={getMenuItems()} align="end" />
                  )
                }
                title={title}
                isTitleInvalid={invalidPhaseIds.some(
                  (invalidId) => invalidId === id,
                )}
                onEditTitle={(title) => onEditTitle({ id, title })}
              />
            }
            onToggleCollapse={() => {}}
          >
            {content}
          </Card>
        );
      })}
      <Button
        icon="plus"
        iconPosition="left"
        disabled={disabled}
        id={newItemAction.id}
        text={newItemAction.text}
        variant="secondary"
        status="info"
        onClick={() => newItemAction.handler()}
      />
      {sortableListModal}
      {removeModal}
    </FlexLayout>
  );
}

StageItems.propTypes = {
  /** Array of item objects */
  items: PropTypes.arrayOf(
    PropTypes.shape({
      /** Item id */
      id: PropTypes.string,
      /** Item title text */
      title: PropTypes.string,
      /** Item content */
      content: PropTypes.node,
    }).isRequired,
  ),
  /** New item action object */
  newItemAction: PropTypes.shape({
    /** Action text */
    text: PropTypes.string,
    /** Action handler */
    handler: PropTypes.func,
  }),
  /** Title for remove item modal */
  removeModalTitle: PropTypes.string,
  /** Title for sort items modal */
  sortModalTitle: PropTypes.string,
  /** Handler for item title edit */
  onEditTitle: PropTypes.func,
  /** Handler for item removal */
  onRemove: PropTypes.func,
  /** Handler for items sort */
  onSort: PropTypes.func,
};

export default StageItems;
